import React, { useState } from 'react';
import {
  BanknotesIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Invoice } from '../../types';
import { BottomSheet } from '../ui/BottomSheet';
import PermissionGate from '../PermissionGate';

export interface CobrosMobileInvoiceCardProps {
  invoice: Invoice;
  formatMoney: (n: number) => string;
  getBalance: (invoice: Invoice) => number;
  statusLabel: (status: string) => string;
  statusClass: (status: string) => string;
  onCollect?: () => void;
  onView: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onPdf?: () => void;
  onMarkPaid?: () => void;
  onDelete?: () => void;
  canCollect: boolean;
  canEdit: boolean;
  canMarkPaid: boolean;
  showCollect?: boolean;
}

export const CobrosMobileInvoiceCard: React.FC<CobrosMobileInvoiceCardProps> = ({
  invoice,
  formatMoney,
  getBalance,
  statusLabel,
  statusClass,
  onCollect,
  onView,
  onEdit,
  onDuplicate,
  onPdf,
  onMarkPaid,
  onDelete,
  canCollect,
  canEdit,
  canMarkPaid,
  showCollect = true,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const bal = getBalance(invoice);
  const number = (invoice as { invoiceNumber?: string }).invoiceNumber || `#${invoice.id}`;
  const clientName = invoice.client?.name || 'N/A';
  const cedula = invoice.client?.cedula || '—';
  const due = invoice.dueDate
    ? new Date(invoice.dueDate).toLocaleDateString('es', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const run = (fn?: () => void) => {
    setMenuOpen(false);
    fn?.();
  };

  return (
    <>
      <article className="rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-150 active:scale-[0.99]">
        <div className="flex items-start gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
            <DocumentTextIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-sm font-semibold text-slate-900">{number}</p>
              <span
                className={clsx(
                  'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1',
                  statusClass(invoice.status)
                )}
              >
                {statusLabel(invoice.status)}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {clientName} · {cedula}
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 border-t border-[#E5EAF1] pt-2.5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Total</p>
            <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-900">
              {formatMoney(Number(invoice.amountDue || 0))}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Saldo</p>
            <p
              className={clsx(
                'mt-0.5 text-xs font-semibold tabular-nums',
                bal > 0 ? 'text-rose-600' : 'text-slate-500'
              )}
            >
              {formatMoney(bal)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Vence</p>
            <p className="mt-0.5 text-xs font-semibold text-slate-900">{due}</p>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          {showCollect && canCollect && onCollect ? (
            <PermissionGate permission="payments.create">
              <button
                type="button"
                onClick={onCollect}
                className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary-600 text-sm font-semibold text-white transition active:scale-[0.98]"
              >
                <BanknotesIcon className="h-4 w-4" />
                Cobrar
              </button>
            </PermissionGate>
          ) : null}
          <button
            type="button"
            onClick={onView}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] text-slate-600 transition hover:bg-slate-50 active:scale-95"
            aria-label="Ver factura"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5EAF1] text-slate-600 transition hover:bg-slate-50 active:scale-95"
            aria-label="Más acciones"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </article>

      <BottomSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Acciones">
        <div className="space-y-0.5 pb-2">
          <MenuBtn label="Ver factura" onClick={() => run(onView)} />
          {canEdit && onEdit ? <MenuBtn label="Editar" onClick={() => run(onEdit)} /> : null}
          {onDuplicate ? <MenuBtn label="Duplicar" onClick={() => run(onDuplicate)} /> : null}
          {onPdf ? <MenuBtn label="Ver PDF" onClick={() => run(onPdf)} /> : null}
          {canMarkPaid && onMarkPaid ? (
            <MenuBtn label="Marcar como pagada" onClick={() => run(onMarkPaid)} />
          ) : null}
          {onDelete ? (
            <>
              <div className="my-2 border-t border-slate-100" />
              <MenuBtn label="Eliminar" danger onClick={() => run(onDelete)} />
            </>
          ) : null}
        </div>
      </BottomSheet>
    </>
  );
};

const MenuBtn: React.FC<{
  label: string;
  onClick: () => void;
  danger?: boolean;
}> = ({ label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={clsx(
      'flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium transition active:scale-[0.99]',
      danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-800 hover:bg-slate-50'
    )}
  >
    {label}
  </button>
);

export default CobrosMobileInvoiceCard;
